import { type ReactNode, useEffect, useRef } from 'react';

import {
  BackPriority,
  doesFocusableExist,
  FocusContext,
  getCurrentFocusKey,
  setFocus,
  useBackHandler,
  useFocusable,
} from '../focus';
import { Button } from './Button';
import { cx } from './cx';
import styles from './Dialog.module.css';

export interface DialogAction {
  key: string;
  label: string;
  primary?: boolean;
  onSelect: () => void;
}

export interface DialogProps {
  open: boolean;
  title: string;
  body?: ReactNode;
  actions: DialogAction[];
  /** Back key and (optionally) actions call this. */
  onClose: () => void;
  focusKey?: string;
  /** Key of the action focused on open (default: first). */
  initialActionKey?: string;
  className?: string;
}

type PanelProps = Omit<DialogProps, 'open' | 'focusKey'> & { focusKey: string };

function DialogPanel({
  title,
  body,
  actions,
  onClose,
  focusKey,
  initialActionKey,
  className,
}: PanelProps) {
  const previous = useRef<string | null>(null);
  const first = initialActionKey ?? actions[0]?.key;
  const { ref, focusKey: key } = useFocusable<HTMLDivElement>({
    focusKey,
    trackChildren: true,
    isFocusBoundary: true,
    preferredChildFocusKey: first ? `${focusKey}-${first}` : undefined,
  });

  useBackHandler(
    () => {
      onClose();
      return true;
    },
    { priority: BackPriority.modal },
  );

  // Remember the opener, focus the first action, restore on unmount.
  useEffect(() => {
    previous.current = getCurrentFocusKey() ?? null;
    if (first) void setFocus(`${focusKey}-${first}`);
    return () => {
      const prev = previous.current;
      if (prev && doesFocusableExist(prev)) void setFocus(prev);
    };
  }, [first, focusKey]);

  return (
    <FocusContext.Provider value={key}>
      <div className={styles.scrim} role="presentation">
        <div
          ref={ref}
          role="dialog"
          aria-modal="true"
          /*
           * UX-306: `aria-label` repeated the title as a string; a screen reader on an Android
           * TV gets the heading and the body themselves this way, which is the ARIA half of the
           * job PR #23 did for the CMS (UX-211).
           */
          aria-labelledby={`${focusKey}-title`}
          {...(body ? { 'aria-describedby': `${focusKey}-body` } : {})}
          data-focus-scope="dialog"
          data-focus-default={first ? `${focusKey}-${first}` : undefined}
          className={cx(styles.panel, className)}
        >
          <h2 id={`${focusKey}-title`} className={styles.title}>
            {title}
          </h2>
          {body ? (
            <div id={`${focusKey}-body`} className={styles.body}>
              {body}
            </div>
          ) : null}
          <div className={styles.actions}>
            {actions.map((action) => (
              <Button
                key={action.key}
                focusKey={`${focusKey}-${action.key}`}
                variant={action.primary ? 'primary' : 'default'}
                onSelect={action.onSelect}
                className={styles.action}
                data-testid={`dialog-${action.key}`}
              >
                {action.label}
              </Button>
            ))}
          </div>
        </div>
      </div>
    </FocusContext.Provider>
  );
}

/**
 * Modal dialog: solid scrim (no blur), focus trapped inside (`isFocusBoundary`), Back closes it
 * via the back chain at `modal` priority, and focus is restored to the opener on close.
 */
export function Dialog({ open, focusKey = 'dialog', ...rest }: DialogProps) {
  if (!open) return null;
  return <DialogPanel focusKey={focusKey} {...rest} />;
}
